import React, { useState } from 'react';
import { authService } from '../services/authService';

interface SSOLoginButtonProps {
  onAuthSuccess?: (user: any) => void;
  onAuthError?: (error: string) => void;
  className?: string;
  children?: React.ReactNode;
  variant?: 'default' | 'compact';
}

const SSOLoginButton: React.FC<SSOLoginButtonProps> = ({
  onAuthSuccess,
  onAuthError,
  className = '',
  children,
  variant = 'default'
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleSSOLogin = async () => {
    setIsLoading(true);
    
    try {
      console.log('🚀 Starting SSO popup authentication...');
      
      // Use the authService popup flow
      await authService.startSSO();
      
      // Listen for auth success
      const handleAuthSuccess = (event: CustomEvent) => {
        console.log('🎉 Auth success event received in button:', event.detail);
        window.removeEventListener('auth:success', handleAuthSuccess as EventListener);
        window.removeEventListener('auth:error', handleAuthError as EventListener);
        window.removeEventListener('auth:timeout', handleAuthTimeout as EventListener);
        
        if (event.detail?.user) {
          console.log('✅ User authenticated:', event.detail.user);
          if (onAuthSuccess) {
            onAuthSuccess(event.detail.user);
          }
        }
        setIsLoading(false);
      };

      const handleAuthError = (event: CustomEvent) => {
        console.error('❌ Auth error event received:', event.detail);
        window.removeEventListener('auth:success', handleAuthSuccess as EventListener);
        window.removeEventListener('auth:error', handleAuthError as EventListener);
        window.removeEventListener('auth:timeout', handleAuthTimeout as EventListener);
        
        const errorMessage = event.detail?.error || 'Authentication failed';
        if (onAuthError) {
          onAuthError(errorMessage);
        }
        setIsLoading(false);
      };

      const handleAuthTimeout = () => {
        console.log('⏰ Auth timeout event received');
        window.removeEventListener('auth:success', handleAuthSuccess as EventListener);
        window.removeEventListener('auth:error', handleAuthError as EventListener);
        window.removeEventListener('auth:timeout', handleAuthTimeout as EventListener);
        
        if (onAuthError) {
          onAuthError('Authentication timeout - please try again');
        }
        setIsLoading(false);
      };

      // Add event listeners
      window.addEventListener('auth:success', handleAuthSuccess as EventListener);
      window.addEventListener('auth:error', handleAuthError as EventListener);
      window.addEventListener('auth:timeout', handleAuthTimeout as EventListener);

    } catch (error) {
      console.error('SSO login error:', error);
      setIsLoading(false);
      
      if (onAuthError) {
        onAuthError(error instanceof Error ? error.message : 'Authentication failed');
      }
    }
  };

  const baseClasses = variant === 'compact' 
    ? 'inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200'
    : 'inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-md transition-colors duration-200';

  const colorClasses = variant === 'compact'
    ? 'text-command-light-blue hover:bg-slate-800 border border-command-light-blue hover:bg-command-light-blue hover:text-slate-900'
    : 'text-white bg-command-dark-blue hover:bg-command-dark-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-command-dark-blue';

  const disabledClasses = 'disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <button
      onClick={handleSSOLogin}
      disabled={isLoading}
      className={`${baseClasses} ${colorClasses} ${disabledClasses} ${className}`}
    >
      {isLoading ? (
        <>
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Connecting...
        </>
      ) : (
        children || (
          <>
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {variant === 'compact' ? 'Login via Forum' : 'Login via Community Forum'}
          </>
        )
      )}
    </button>
  );
};

export default SSOLoginButton;